import { Box, ChartLegend, EsgScore, SentimentBar, Typography } from '@scope/scope-one-ui/core';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { COLORS } from '@scope/scope-one-ui/theme';
import { Period } from '@scope/scope-one-ui/core/SentimentBar/SentimentBar.d';
import { EsgSentimentParams } from 'modules/ESGSentiment/types/esgSentiment';
import {
  SentimentBreakdown,
  SentimentCharts,
  SentimentHeader,
  SentimentScore,
  VerticalArrow,
  VerticalArrowBorderLeft,
  VerticalArrowBorderRight,
  VerticalArrowContainer,
  VerticalDivider,
} from './ScoreBreakdownWidget.styled';
import {
  useGetAttentionBuzzBreakdown,
  useGetPeriodIndustryScore,
  useGetPeriodScore,
} from '../../services/esgSentiment';
import { AttentionBuzz } from '../AttentionBuzz';

const ScoreBreakdownWidget = () => {
  const { entityId } = useParams<EsgSentimentParams>();
  const [filter, setFilter] = useState<Period>('0day');
  const { data: periodScores, loading: periodScoresLoading } = useGetPeriodScore(entityId!, filter);
  const { data: periodIndustryScores, loading: periodIndustryScoresLoading } =
    useGetPeriodIndustryScore(entityId!, filter);
  const { data: attentionBuzzBreakdown, loading: attentionBuzzBreakdownLoading } =
    useGetAttentionBuzzBreakdown(entityId!, filter);

  const legendItems = [
    { label: 'Peer group worst', color: `${COLORS.error.main}` },
    { label: 'Peer group average', color: `${COLORS.warning.main}` },
    { label: 'Peer group best', color: `${COLORS.success.main}` },
  ];

  return (
    <Box display="flex">
      <SentimentScore display="flex" flexDirection="column" justifyContent="center" pl={4} pr={2}>
        <Box my={4} display="flex" justifyContent="center">
          <EsgScore
            value={periodScores?.esgScore || 0}
            worst={periodIndustryScores?.industry.esg.worst || 0}
            average={periodIndustryScores?.industry.esg.average || 0}
            best={periodIndustryScores?.industry.esg.best || 0}
            label="ESG Sentiment Score"
            loading={periodScoresLoading && periodIndustryScoresLoading}
          />
        </Box>
        <SentimentBar
          period={filter}
          onPeriodChange={setFilter}
          negative={attentionBuzzBreakdown?.events.negative || 0}
          neutral={attentionBuzzBreakdown?.events.neutral || 0}
          positive={attentionBuzzBreakdown?.events.positive || 0}
          loading={attentionBuzzBreakdownLoading}
        />
        <Box display="flex" mt={2} justifyContent="center">
          <AttentionBuzz
            loading={attentionBuzzBreakdownLoading}
            value={attentionBuzzBreakdown?.attentionBuzz.value}
            notApplicable={filter === 'max'}
            label={attentionBuzzBreakdown?.attentionBuzz.label}
          />
        </Box>
      </SentimentScore>
      <VerticalArrowContainer>
        <VerticalArrowBorderLeft />
        <VerticalArrow />
        <VerticalArrowBorderRight />
      </VerticalArrowContainer>
      <SentimentBreakdown>
        <Box display="flex" flexDirection="column" gridGap={4}>
          <SentimentHeader py={2}>
            <Typography variant="h2">Environment</Typography>
            <Typography variant="h2">Social</Typography>
            <Typography variant="h2">Governance</Typography>
          </SentimentHeader>
          <SentimentCharts>
            <EsgScore
              value={periodScores?.scores.e || 0}
              worst={periodIndustryScores?.industry.e.worst || 0}
              average={periodIndustryScores?.industry.e.average || 0}
              best={periodIndustryScores?.industry.e.best || 0}
              loading={periodScoresLoading && periodIndustryScoresLoading}
            />
            <VerticalDivider />
            <EsgScore
              value={periodScores?.scores.s || 0}
              worst={periodIndustryScores?.industry.s.worst || 0}
              average={periodIndustryScores?.industry.s.average || 0}
              best={periodIndustryScores?.industry.s.best || 0}
              loading={periodScoresLoading && periodIndustryScoresLoading}
            />
            <VerticalDivider />
            <EsgScore
              value={periodScores?.scores.g || 0}
              worst={periodIndustryScores?.industry.g.worst || 0}
              average={periodIndustryScores?.industry.g.average || 0}
              best={periodIndustryScores?.industry.g.best || 0}
              loading={periodScoresLoading && periodIndustryScoresLoading}
            />
          </SentimentCharts>
          <Box display="flex" justifyContent="center" mb={2}>
            <ChartLegend size="semiMedium" items={legendItems} />
          </Box>
        </Box>
      </SentimentBreakdown>
    </Box>
  );
};

export default ScoreBreakdownWidget;
